/**
 * route-manifest.ts — pure data describing the explicit routes added by
 * Theme A of the Pre-Testing Remediation Pass.
 *
 * Lives in a JSX-free module so plain-TS consumers (the Cmd+K palette
 * builder, the WorkPanel sidebar nav, smoke tests) can import it without
 * dragging in lazy()-loaded components or React's JSX runtime. The
 * matching `<Route>` elements are wired in App.tsx; the lazy components
 * live in routes.tsx.
 *
 * Single source of truth for navigation entries: editing this manifest
 * updates the explicit React-Router routes, the Cmd+K palette, AND the
 * WorkPanel sidebar nav.
 */

export interface RouteEntry {
  path: string;
  label: string;
  section: "Navigate" | "Insights" | "Admin";
  hint?: string;
}

export const ROUTE_MANIFEST: RouteEntry[] = [
  // Organizer surfaces — the operator's primary workspace nav.
  { path: "/today", label: "Today", section: "Navigate", hint: "Today's plan" },
  { path: "/inbox", label: "Inbox", section: "Navigate", hint: "Unified inbox" },
  { path: "/upcoming", label: "Upcoming", section: "Navigate", hint: "Next 7 days" },
  { path: "/areas", label: "Areas", section: "Navigate", hint: "Ongoing responsibilities" },
  { path: "/projects", label: "Projects", section: "Navigate", hint: "Active projects" },
  { path: "/comms", label: "Comms", section: "Navigate", hint: "Communications hub" },
  { path: "/canvas", label: "Canvas", section: "Navigate", hint: "Dynamic canvas" },

  // Trust / transparency surfaces.
  { path: "/transparency/decisions", label: "Decision Log", section: "Insights" },
  { path: "/transparency/retrospective", label: "Weekly Retrospective", section: "Insights" },
  { path: "/transparency/autonomy", label: "Autonomy Telemetry", section: "Insights" },
  { path: "/transparency/operator-model", label: "Operator Model", section: "Insights" },
  // R1.10 (LA-022 / LA-024) — per-session token-budget observability surface.
  { path: "/transparency/context-inspector", label: "Context Inspector", section: "Insights" },

  // Admin / operator-secondary pages. Reachable via Cmd+K only.
  // Paths intentionally avoid the legacy alias map in settingsCatalog.tsx
  // (e.g. `/admin/approvals`, `/admin/capabilities`) which still 301s
  // those tokens to /settings.
  { path: "/admin/dashboard", label: "Admin Dashboard", section: "Admin" },
  { path: "/admin/approval-queue", label: "Approval Queue", section: "Admin" },
  { path: "/admin/challenge-queue", label: "Challenge Queue", section: "Admin" },
  { path: "/admin/agent-capabilities", label: "Agent Capabilities", section: "Admin" },
  { path: "/admin/agent-email-page", label: "Agent Email", section: "Admin" },
  { path: "/admin/agent-wallet", label: "Agent Wallet", section: "Admin" },
  { path: "/admin/audit", label: "Audit Log", section: "Admin" },
  { path: "/admin/browser-access", label: "Browser Access", section: "Admin" },
  { path: "/admin/config-editor", label: "Config Editor", section: "Admin" },
  { path: "/admin/context-files", label: "Context Files", section: "Admin" },
  { path: "/admin/credentials", label: "Credentials", section: "Admin" },
  { path: "/admin/data-sources-page", label: "Data Sources", section: "Admin" },
  { path: "/admin/dev-loop", label: "Dev Loop", section: "Admin" },
  { path: "/admin/email-drafts", label: "Email Drafts", section: "Admin" },
  { path: "/admin/injection-defense", label: "Injection Defense", section: "Admin" },
  { path: "/admin/mandates", label: "Mandates", section: "Admin" },
  { path: "/admin/middleware-config", label: "Middleware Config", section: "Admin" },
  { path: "/admin/models", label: "Models", section: "Admin" },
  { path: "/admin/optimizer", label: "Optimizer", section: "Admin" },
  { path: "/admin/plugin-manager", label: "Plugin Manager", section: "Admin" },
  { path: "/admin/rate-limits", label: "Rate Limits", section: "Admin" },
  { path: "/admin/relationship-graph", label: "Relationship Graph", section: "Admin" },
  { path: "/admin/security-observability", label: "Security Observability", section: "Admin" },
  { path: "/admin/services", label: "Services", section: "Admin" },
  { path: "/admin/system-health", label: "System Health", section: "Admin" },
  { path: "/admin/setup-wizard", label: "Setup Wizard", section: "Admin" },
  { path: "/admin/workspace-access-page", label: "Workspace Access", section: "Admin" },
  { path: "/admin/workspace-zones", label: "Workspace Zones", section: "Admin" },
];
